import { DEFAULT_CHUNK_SIZE } from "@custom-types/upload-manager-types";
import SparkMD5 from "spark-md5";
/**
 * Chunked MD5 calculation based on SparkMD5, correctly handling out-of-order chunks by queueing them.
 */
export class ChunkedMD5 {
  #spark = new SparkMD5.ArrayBuffer();
  #queue = [];
  #nextStartByte = 0;

  /**
   * @param file Input file.
   * @param abortSignal Optional signal to abort the operation.
   * @returns Promise for the MD5-hash of the provided file.
   *
   * @throws Error if the file could not be read, or if the operation was aborted.
   */
  static async fromFile(file, abortSignal) {
    const spark = new SparkMD5.ArrayBuffer();
    const chunkSize = DEFAULT_CHUNK_SIZE;
    for (let startByte = 0; startByte < file.size; startByte += chunkSize) {
      abortSignal?.throwIfAborted();
      const endByte = Math.min(startByte + chunkSize, file.size);
      const blob = file.slice(startByte, endByte);
      const blobData = await blob.arrayBuffer();
      spark.append(blobData);
    }
    return spark.end();
  }

  /**
   * Append a chunk for MD5 calculation.
   * The chunks can be added out-of-order, but the missing chunk(s) should be added soon to avoid increased
   * memory usage due to queueing.
   *
   * @param blobData Chunk (binary data).
   * @param startByte Start byte of the chunk in the file.
   * @throws {Error} When trying to add a previous chunk again.
   */
  append(blobData, startByte) {
    const endByte = startByte + blobData.byteLength;
    if (startByte === this.#nextStartByte) {
      this.#spark.append(blobData);
      this.#nextStartByte = endByte;
      if (this.#queue.length > 0) {
        this.#processQueue();
      }
    } else if (startByte < this.#nextStartByte) {
      throw new Error(`ChunkedMD5: Trying to add earlier chunk with startByte ${startByte}; ` + `expected instead >= ${this.#nextStartByte}.`);
    } else {
      this.#queue.push({
        blobData,
        startByte,
        endByte
      });
    }
  }

  /**
   * Finish the MD5 calculation and return the hash.
   * @returns Hex string with MD5 hash.
   * @throws {Error} If chunks in between were missing.
   */
  end() {
    if (this.#queue.length > 0) {
      throw new Error(`ChunkedMD5: Cannot calculate hash. Missing chunk with startByte ${this.#nextStartByte}.`);
    }
    return this.#spark.end();
  }

  /**
   * Process the queued chunks, adding as many as possible to the MD5 calculation and removing them from the queue.
   */
  #processQueue() {
    let chunk;
    while (true) {
      chunk = undefined;
      for (let i = 0; i < this.#queue.length; i++) {
        if (this.#queue[i].startByte === this.#nextStartByte) {
          chunk = this.#queue[i];
          this.#queue.splice(i, 1);
          break;
        }
      }
      if (!chunk) {
        break;
      }
      this.#spark.append(chunk.blobData);
      this.#nextStartByte = chunk.endByte;
    }
  }
}